import React, { useState, useRef, useMemo, useEffect } from 'react';
import { styled, Col, Row, Title, Modal, Input } from '@teko/ds-desktop';
import { CloseOutlined, SearchOutlined, CheckOutlined } from '@teko/ds-icon';
import { ListContent, SubTitle, Caption, Body, BottomSheet } from '@teko/ds-mobile';
import { normalizeText, ConditionalWrapper } from '@teko/utils';
import { t } from '../../i18n.es.js';

const StyledListContent = styled(ListContent)`
  cursor: pointer;
  height: 3rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;
const LocationPicker = ({
  isShowed,
  setShowed,
  locations,
  selectedLocation,
  onSelectLocation,
  isMobile,
  hideSearchBox
}) => {
  const [keyword, setKeyword] = useState("");
  const listRef = useRef();
  const selectedLocationRef = useRef(null);
  const locationsForSearching = useMemo(() => {
    const keywordLocationMap = /* @__PURE__ */ new Map();
    locations.forEach((location) => {
      const normalizedLocationName = normalizeText(location.locationName, true);
      const _locations = keywordLocationMap.get(normalizedLocationName);
      if (_locations) {
        _locations.push(location);
      } else {
        keywordLocationMap.set(normalizedLocationName, [location]);
      }
    });
    return keywordLocationMap;
  }, [locations]);
  const showedLocations = useMemo(() => {
    const keywords = Array.from(locationsForSearching.keys());
    const normalizedKeyword = normalizeText(keyword, true);
    const result = [];
    keywords.forEach((keyword2) => {
      if (keyword2.includes(normalizedKeyword)) {
        const locations2 = locationsForSearching.get(keyword2);
        if (locations2) {
          result.push(...locations2);
        }
      }
    });
    return result;
  }, [keyword, locationsForSearching]);
  useEffect(() => {
    if (!isShowed) {
      setKeyword("");
    } else {
      setTimeout(() => {
        if (listRef.current && selectedLocationRef.current) {
          const listRect = listRef.current.getBoundingClientRect();
          const selectedLocationRect = selectedLocationRef.current.getBoundingClientRect();
          listRef.current.scrollBy({
            top: selectedLocationRect.y - listRect.y - listRect.height / 4,
            behavior: "smooth"
          });
        }
      }, 500);
    }
  }, [isShowed]);
  return /* @__PURE__ */ React.createElement(ConditionalWrapper, {
    wrapper: StyledModal,
    condition: !isMobile,
    visible: isShowed,
    onClose: () => {
      setShowed(false);
    },
    footer: false,
    showCloseIcon: false
  }, /* @__PURE__ */ React.createElement(ConditionalWrapper, {
    wrapper: StyledBottomSheet,
    condition: !!isMobile,
    show: isShowed,
    closeBottomSheet: () => {
      setShowed(false);
    }
  }, /* @__PURE__ */ React.createElement(Header, {
    justify: "center"
  }, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(Row, {
    justify: "center"
  }, !isMobile ? /* @__PURE__ */ React.createElement(Title, {
    weight: "medium",
    level: 3
  }, t("Ch\u1ECDn khu v\u1EF1c nh\u1EADn h\xE0ng")) : /* @__PURE__ */ React.createElement(SubTitle, {
    weight: "medium",
    level: 2
  }, t("Ch\u1ECDn khu v\u1EF1c nh\u1EADn h\xE0ng"))), /* @__PURE__ */ React.createElement(Row, {
    justify: "center"
  }, /* @__PURE__ */ React.createElement(Caption, {
    level: 1,
    weight: "medium",
    color: "textSecondary"
  }, t("Vui lo\u0300ng cho\u0323n khu v\u01B0\u0323c nh\xE2\u0323n ha\u0300ng c\u1EE7a b\u1EA1n")))), /* @__PURE__ */ React.createElement(CloseIconContainer, {
    onClick: () => {
      setShowed(false);
    },
    isMobile
  }, /* @__PURE__ */ React.createElement(CloseOutlined, {
    size: "1.5rem"
  }))), !hideSearchBox && /* @__PURE__ */ React.createElement(InputContainer, null, /* @__PURE__ */ React.createElement(StyledInput, {
    prefix: /* @__PURE__ */ React.createElement(SearchOutlined, null),
    placeholder: t("T\xECm ki\u1EBFm T\u1EC9nh/ Th\xE0nh ph\u1ED1"),
    value: keyword,
    onChange: setKeyword,
    isMobile
  })), /* @__PURE__ */ React.createElement(List, {
    ref: (ref) => {
      if (!ref) {
        return;
      }
      if (ref.scrollHeight > ref.clientHeight) {
        ref.style.paddingRight = "0";
      }
      listRef.current = ref;
    },
    isMobile
  }, showedLocations.length ? showedLocations.map((location, i, arr) => {
    const isSelected = location === selectedLocation;
    return /* @__PURE__ */ React.createElement(StyledListContent, {
      key: location.locationCode,
      label: location.locationName,
      hasDivider: i < arr.length - 1,
      onClick: () => {
        onSelectLocation(location);
      },
      extra: isSelected ? /* @__PURE__ */ React.createElement(CheckOutlined, {
        color: "primary500",
        size: "1.5rem"
      }) : void 0,
      ref: isSelected ? selectedLocationRef : void 0,
      "data-testid": isSelected ? "selectedItem" : void 0
    });
  }) : /* @__PURE__ */ React.createElement(Row, {
    justify: "center",
    align: "middle"
  }, /* @__PURE__ */ React.createElement(Body, {
    color: "placeholder",
    level: 1,
    weight: "regular"
  }, t("Kh\xF4ng t\xECm th\u1EA5y t\u1EC9nh th\xE0nh ph\u1ED1 n\xE0o !!"))))));
};
const StyledModal = styled(Modal)`
  .teko-modal-content {
    padding: 1rem 0;
    width: 32.5rem;
  }
`;
const Header = styled(Row)`
  position: relative;
  padding: 0.5rem 1rem;
`;
const CloseIconContainer = styled(Col)`
  position: absolute !important;
  ${({ isMobile }) => !isMobile ? "right" : "left"}: 1.375rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;
const InputContainer = styled(Row)`
  padding: 0.5rem 1rem;
`;
const StyledInput = styled(Input)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ isMobile }) => !isMobile ? "0 1rem" : "0"};
  border-radius: 4px;

  .input-container {
    border: none;
  }
`;
const List = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: min(70vh, 28rem);
  padding: 0 0.75rem;
  padding-bottom: ${({ isMobile }) => isMobile ? "0.75rem" : "0"};

  ::-webkit-scrollbar {
    width: 0.75rem;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.disable};
    background-clip: content-box;
    border-radius: 100px;
    border: 0.25rem solid transparent;
    width: 0.25rem;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.placeholder};
  }
`;
const StyledBottomSheet = styled(BottomSheet)`
  width: 100%;
  z-index: 1060;

  & > div {
    max-height: 100vh;
    overflow: auto;
  }
`;

export { LocationPicker };
